import { useCallback, useMemo } from "react";
import { splitIntoInsightBuckets } from "@/components/AiInsightsCard";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import { toInsightLines } from "@/utils/insightActionPlanDefaults";
import { useMistralFirstInsights } from "@/hooks/useMistralFirstInsights";
import { parseInsightBullets } from "@/utils/parseInsightBullets";
import { bulletsToTextList } from "@/utils/mistralInsightMappers";
import {
  BarChart3,
  BookOpen,
  Check,
  ClipboardList,
  Clock,
  Eye,
  GitBranch,
  Lightbulb,
  PlusSquare,
  RefreshCw,
  Shield,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  TrendingDown,
  TrendingUp,
  User,
  UserPlus,
  Users,
} from "lucide-react";
import {
  buildAgeInsightsHero,
  buildAgeInsightsKeyInsights,
  buildAgeInsightsMarkdown,
  buildAgeInsightsNarrative,
  buildAgeInsightsPriorityActions,
  buildAgeInsightsRecommendations,
  buildAgeInsightsRootCauses,
  buildAgeInsightsWatchlist,
} from "@/utils/ageEnrolmentDerived";
import "./AgeEnrolmentInsightsTab.css";

const HERO_ICONS = {
  "": Users,
  green: BarChart3,
  orange: TrendingUp,
  purple: ShieldCheck,
};

const ROOT_ICONS = {
  clock: Clock,
  user: User,
  chart: BarChart3,
  stairs: TrendingDown,
};

const RECOMMEND_ICONS = {
  trend: TrendingUp,
  "user-plus": UserPlus,
  refresh: RefreshCw,
  plus: PlusSquare,
};

const NARRATIVE_ICONS = {
  chart: BarChart3,
  shield: Shield,
  bars: BarChart3,
};

const WATCH_ICONS = {
  "": BarChart3,
  pink: GitBranch,
  green: ShieldCheck,
};

const CheckInsightRow = ({ segments = [] }) => (
  <div className="aein-check-row">
    <span className="aein-check">
      <Check size={12} strokeWidth={3} />
    </span>
    <span>
      {segments.map((part, idx) =>
        part.bold ? <b key={idx}>{part.text}</b> : <span key={idx}>{part.text}</span>
      )}
    </span>
  </div>
);

const AgeEnrolmentInsightsTab = ({ overview = {}, ageData = [], blockData = [] }) => {
  const hasData = Boolean(overview?.total_enrolment);

  const buildLocalMarkdown = useCallback(
    () => buildAgeInsightsMarkdown(overview, ageData, blockData),
    [overview, ageData, blockData]
  );

  const scopeKey = useMemo(
    () => JSON.stringify({ e: overview?.total_enrolment, p: overview?.peak_enrolment_age }),
    [overview?.total_enrolment, overview?.peak_enrolment_age]
  );

  const { markdown: aiMarkdown, loading: insightsLoading, isMistral } = useMistralFirstInsights({
    kind: "age-enrolment",
    title: "Age Enrolment Insights",
    buildLocalMarkdown,
    context: { overview, ageData, blockData },
    enabled: hasData,
    scopeKey,
  });

  const buckets = useMemo(() => splitIntoInsightBuckets(aiMarkdown), [aiMarkdown]);

  const localHero = useMemo(() => buildAgeInsightsHero(overview), [overview]);
  const hero = useMemo(() => {
    if (insightsLoading) return localHero;
    const summary = parseInsightBullets(buckets.insights)[0];
    return summary ? { ...localHero, summary } : localHero;
  }, [insightsLoading, buckets.insights, localHero]);

  const localKeyInsights = useMemo(
    () => buildAgeInsightsKeyInsights(overview, ageData),
    [overview, ageData]
  );
  const keyInsights = useMemo(() => {
    if (insightsLoading) return [];
    const bullets = parseInsightBullets(buckets.insights);
    if (!bullets.length) return localKeyInsights;
    return bullets.slice(0, 4).map((text) => ({ segments: [{ text }] }));
  }, [insightsLoading, buckets.insights, localKeyInsights]);

  const localRootCauses = useMemo(() => buildAgeInsightsRootCauses(overview), [overview]);
  const rootCauses = useMemo(() => {
    if (insightsLoading) return [];
    const aiItems = bulletsToTextList(parseInsightBullets(buckets.root), localRootCauses);
    return aiItems?.length ? aiItems : localRootCauses;
  }, [insightsLoading, buckets.root, localRootCauses]);

  const localRecommendations = useMemo(
    () => buildAgeInsightsRecommendations(overview, ageData, blockData),
    [overview, ageData, blockData]
  );
  const recommendations = useMemo(() => {
    if (insightsLoading) return [];
    const aiItems = bulletsToTextList(parseInsightBullets(buckets.recs), localRecommendations);
    return aiItems?.length ? aiItems : localRecommendations;
  }, [insightsLoading, buckets.recs, localRecommendations]);

  const localPriorityActions = useMemo(
    () => buildAgeInsightsPriorityActions(overview, blockData),
    [overview, blockData]
  );
  const priorityActions = useMemo(() => {
    if (insightsLoading) return [];
    const aiItems = bulletsToTextList(parseInsightBullets(buckets.actions), localPriorityActions);
    return aiItems?.length ? aiItems : localPriorityActions;
  }, [insightsLoading, buckets.actions, localPriorityActions]);

  const localNarrative = useMemo(() => buildAgeInsightsNarrative(overview), [overview]);
  const narrative = useMemo(() => {
    if (insightsLoading) return [];
    const bullets = parseInsightBullets(buckets.recs);
    if (!bullets.length) return localNarrative;
    return bullets.slice(0, 3).map((text, i) => {
      const local = localNarrative[i] || {};
      return {
        icon: local.icon || "chart",
        title: text.split(/[:.]/)[0].slice(0, 50) || local.title || `Insight ${i + 1}`,
        body: text,
      };
    });
  }, [insightsLoading, buckets.recs, localNarrative]);

  const watchlist = useMemo(
    () => buildAgeInsightsWatchlist(overview, blockData),
    [overview, blockData]
  );

  const defaultRoot = useMemo(() => toInsightLines(localRootCauses), [localRootCauses]);
  const defaultRecs = useMemo(() => toInsightLines(localRecommendations), [localRecommendations]);
  const defaultActions = useMemo(() => toInsightLines(localPriorityActions), [localPriorityActions]);

  return (
    <div className="aein-tab" data-testid="age-enrolment-insights">
      <section className="aein-insight-hero">
        <div className="aein-hero-top">
          <div className="aein-sparkle-orb" aria-hidden="true">
            <Sparkles className="aein-large-icon" strokeWidth={2} />
          </div>
          <div>
            <div className="aein-hero-title-row">
              <h2>AI Insights Summary</h2>
              <span className="aein-ai-chip">
                ✦ {insightsLoading ? "Generating…" : isMistral ? "Mistral AI" : "Rule-based"}
              </span>
            </div>
            <p>{hero.summary}</p>
          </div>
        </div>

        <div className="aein-hero-metrics" aria-label="Insights summary metrics">
          {hero.metrics.map((metric) => {
            const Icon = HERO_ICONS[metric.iconClass] ?? Users;
            return (
              <article key={metric.label} className="aein-metric-card">
                <div className={`aein-metric-icon ${metric.iconClass}`.trim()}>
                  <Icon className="aein-svg-icon" strokeWidth={2} />
                </div>
                <div>
                  <small>{metric.label}</small>
                  <strong>{metric.value}</strong>
                  <span>{metric.sub}</span>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="aein-three-grid">
        <article className="aein-panel">
          <div className="aein-panel-head">
            <div className="aein-panel-title">
              <div className="aein-small-icon">
                <Lightbulb className="aein-svg-icon" strokeWidth={2} />
              </div>
              <div>
                <h3>Key Insights</h3>
                <p>What the data says</p>
              </div>
            </div>
          </div>
          <div className="aein-check-list">
            {keyInsights.map((row, idx) => (
              <CheckInsightRow key={idx} segments={row.segments} />
            ))}
          </div>
        </article>

        <article className="aein-panel">
          <div className="aein-panel-head">
            <div className="aein-panel-title">
              <div className="aein-small-icon pink">
                <Target className="aein-svg-icon" strokeWidth={2} />
              </div>
              <div>
                <h3>Root Cause Signals</h3>
                <p>Risks to investigate</p>
              </div>
            </div>
          </div>
          <div className="aein-signal-list">
            {rootCauses.map((item) => {
              const Icon = ROOT_ICONS[item.icon] ?? Clock;
              return (
                <div key={item.text} className="aein-signal-row">
                  <div className="aein-mini-tile">
                    <Icon className="aein-svg-icon" strokeWidth={2} />
                  </div>
                  <span>{item.text}</span>
                </div>
              );
            })}
          </div>
        </article>

        <article className="aein-panel">
          <div className="aein-panel-head">
            <div className="aein-panel-title">
              <div className="aein-small-icon green">
                <Star className="aein-svg-icon" strokeWidth={2} />
              </div>
              <div>
                <h3>Recommendations</h3>
                <p>Next best actions</p>
              </div>
            </div>
          </div>
          <div className="aein-recommend-list">
            {recommendations.map((item) => {
              const Icon = RECOMMEND_ICONS[item.icon] ?? TrendingUp;
              return (
                <div key={item.text} className="aein-recommend-row">
                  <div className="aein-mini-tile green">
                    <Icon className="aein-svg-icon" strokeWidth={2} />
                  </div>
                  <span>{item.text}</span>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <section className="aein-action-plan">
        <InterventionActionPlan
          aiContent={aiMarkdown}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <section className="aein-two-grid">
        <article className="aein-panel">
          <div className="aein-panel-head">
            <div className="aein-panel-title">
              <div className="aein-small-icon">
                <ClipboardList className="aein-svg-icon" strokeWidth={2} />
              </div>
              <div>
                <h3>Priority Action Items</h3>
                <p>District-level follow-up queue</p>
              </div>
            </div>
          </div>
          <div className="aein-action-list">
            {priorityActions.map((item) => (
              <div key={item.num} className="aein-action-row">
                <span className="aein-num">{item.num}</span>
                <span>{item.text}</span>
                <span className={`aein-priority ${item.priority}`}>
                  {item.priority === "high" ? "High" : "Medium"}
                </span>
              </div>
            ))}
          </div>
        </article>

        <article className="aein-panel">
          <div className="aein-panel-head">
            <div className="aein-panel-title">
              <div className="aein-small-icon purple">
                <BookOpen className="aein-svg-icon" strokeWidth={2} />
              </div>
              <div>
                <h3>Insight Narrative / What This Means</h3>
                <p>Policy interpretation</p>
              </div>
            </div>
          </div>
          <div className="aein-narrative-list">
            {narrative.map((item) => {
              const Icon = NARRATIVE_ICONS[item.icon] ?? BarChart3;
              return (
                <div key={item.title} className="aein-narrative-row">
                  <div className="aein-mini-tile purple">
                    <Icon className="aein-svg-icon" strokeWidth={2} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <section className="aein-watchlist" aria-label="Suggested next review watchlist">
        <div className="aein-watch-title">
          <span className="aein-watch-icon">
            <Eye className="aein-svg-icon" strokeWidth={2} />
          </span>
          Suggested Next Review / Watchlist
        </div>
        {watchlist.map((item) => {
          const Icon = WATCH_ICONS[item.iconClass] ?? BarChart3;
          return (
            <div key={item.text} className={`aein-watch-chip ${item.iconClass}`.trim()}>
              <Icon className="aein-svg-icon" strokeWidth={2} />
              {item.text}
            </div>
          );
        })}
      </section>
    </div>
  );
};

export default AgeEnrolmentInsightsTab;
